import React, { useEffect, useState } from 'react';
import moment from 'moment';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import CustomCheckbox from 'components/CustomCheckbox/CustomCheckbox';
import CustomDatePicker from 'components/CustomDatePicker/CustomDatePicker';
import Account from '@material-ui/icons/AccountCircleOutlined';
import { translate } from 'components/Translation';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { useDispatch } from 'react-redux';
import { throwError } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { UsersService } from 'services/users/users';
import styles from 'assets/jss/material-dashboard-pro-react/components/editExpirationDateDialogStyle';
import { makeStyles } from '@material-ui/core';
import { isValidDate } from 'utils/date/validateDate';
import { BodyS, HeadingSBold } from 'components/Typography/Typography';

const useStyles = makeStyles(styles);

interface IEditExpirationDateDialogProps {
	isDialogOpen: boolean;
	setIsDialogOpen: (isDialogOpen: boolean) => void;
	data: types.organizations.IExpirationDialogData;
	departmentId: string;
	expirationDateEdited: (userId: string, permissionExpiration: string) => void;
}

const EditExpirationDateDialog = (props: IEditExpirationDateDialogProps) => {
	const { isDialogOpen, setIsDialogOpen, data, departmentId, expirationDateEdited } = props;
	const [expirationDate, setExpirationDate] = useState<moment.Moment | null>(null);
	const [unlimitedExpirationDate, setUnlimitedExpirationDate] = useState(true);
	const classes = useStyles();
	const dispatch = useDispatch();

	const clearDialog = () => {
		if (data) {
			setExpirationDate(moment(data.expirationDate));
			setUnlimitedExpirationDate(!data.expirationDate);
		}
	};

	useEffect(() => {
		if (data) {
			setExpirationDate(moment(data.expirationDate));
			setUnlimitedExpirationDate(!data.expirationDate);
		}
	}, [data]);

	const hideDialog = () => {
		setIsDialogOpen(false);
		clearDialog();
	};

	const editExpirationDate = () => {
		dispatch(showLoadingIndicator());
		const formattedExpirationDate: string = expirationDate?.format('YYYY-MM-DD') || '';
		const permissionExpiration: string = !unlimitedExpirationDate ? formattedExpirationDate : '';
		UsersService.updateCorpUserPermissions({
			userId: data.userId,
			override: false,
			permissions: [
				{
					type: 'D',
					departmentId,
					permissionExpiration,
				},
			],
		})
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(
				({ response }) => {
					if (response.updateCorpUserPermissionsResponse.result) {
						hideDialog();
						if (expirationDateEdited) {
							expirationDateEdited(data.userId, permissionExpiration);
						}
					}
				},
				(error) => throwError(error)
			);
	};

	return (
		<CustomDialog
			isOpen={isDialogOpen}
			hideDialog={hideDialog}
			className={classes.dialogWrapper}
			header={translate('department.edit.expiration.date')}
			rightSideButtons={[
				{
					text: translate('cancel'),
					onClick: hideDialog,
					color: 'tertiary-light' as types.theme.buttonColor,
				},
				{
					text: translate('save'),
					onClick: editExpirationDate,
					color: 'primary-light' as types.theme.buttonColor,
					disabled: !unlimitedExpirationDate && !expirationDate,
				},
			]}
			dialogBody={
				<div className={classes.dialogBodyWrapper}>
					<div className={classes.userInfoWrapper}>
						<div>
							<Account />
						</div>
						<div>
							<HeadingSBold color="primary-light">{data && data.userName}</HeadingSBold>
							<BodyS>{translate('manager')}</BodyS>
						</div>
					</div>
					<div className={classes.expirationDateWrapper}>
						<BodyS color="secondary-light">{translate('select.expiration.date')}</BodyS>
						<div>
							<CustomDatePicker
								timeFormat={false}
								value={expirationDate}
								onChange={(date: moment.Moment | string) => {
									if (moment.isMoment(date)) {
										setExpirationDate(date);
									}
								}}
								inputProps={{
									placeholder: translate('department.details.expiration.date'),
								}}
								isValidDate={(currentDate: moment.Moment) => isValidDate(currentDate, 'yesterday')}
							/>
							<CustomCheckbox
								label={translate('unlimited')}
								checked={unlimitedExpirationDate}
								onClick={() => setUnlimitedExpirationDate((value) => !value)}
							/>
						</div>
					</div>
				</div>
			}
		/>
	);
};

export default EditExpirationDateDialog;
